import React, { useState, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import { step1Questions, initialAllowancesQuestions } from './data';
import FormConsumer from '../../collect-info/FormConsumers';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function Version2018({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  formatFirstNameMiddleInitial,
  resetInitialQuestions,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  prefilledData.employee_first_name_middle_initial = formatFirstNameMiddleInitial();

  const step1Form = useRef();
  const step1InitialQuestions = getInitialQuestions(step1Questions);
  const [stepOneQuestions, setStepOneQuestions] = useState(step1InitialQuestions);

  const [forYourself, setForYourself] = useState(data.for_yourself || false);
  const [onlyOneJob, setOnlyOneJob] = useState(data.only_one_job || false);
  const [forSpouse, setForSpouse] = useState(data.for_spouse || false);
  const [fileHeadOfHousehold, setFileHeadOfHousehold] = useState(data.file_head_of_household || false);
  const [itemizedDeductions, setItemizedDeductions] = useState(data.large_itemized_deductions || false);

  const allowancesForm = useRef();
  const allowancesQuestionsWithAnswers = getInitialQuestions(initialAllowancesQuestions);
  const [allowancesQuestions, setAllowancesQuestions] = useState(allowancesQuestionsWithAnswers);

  const [exemptionsFromWithholding, setExemptionsFromWithholding] = useState(data.exemption || '');

  // to calculate total number of allowances
  const calculateTotalNoAllowances = () => {
    let value = 0;

    if (forYourself) value += 1;
    if (onlyOneJob) value += 1;
    if (forSpouse) value += 1;
    if (fileHeadOfHousehold) value += 1;
    if (itemizedDeductions) value += 1;

    const { dependents } = allowancesQuestions[0].answer.answer;
    if (/^\d+$/.test(dependents)) value += parseInt(dependents);

    return value;
  };

  const mustCompleteAllowances = () => (
    exemptionsFromWithholding === 'member_of_registered_tribe'
      || exemptionsFromWithholding === 'member_of_reserve_national_guard'
      || exemptionsFromWithholding === 'not_applicable'
      || exemptionsFromWithholding === ''
  );

  const renderStep1Form = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={stepOneQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = stepOneQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setStepOneQuestions(questions);
        }}
        ref={step1Form}
      />
    </div>
  );

  const renderExemptionsFromWithholding = (isReviewStep) => (
    <Flex column mb="20px">
      <Text bold fontSize="14px" mb="12px">
        Exemptions from withholding
      </Text>
      <S.QuestionWrapper>
        <Flex mb="7px">
          <S.QuestionLabel>
            You may be entitled to claim an exemption from Montana income tax withholding.
            If applicable, mark one box below to indicate the reason why you believe you are exempt.
          </S.QuestionLabel>
        </Flex>
        <label>
          <Flex mb="8px">
            <S.Input
              style={{ height: '22px' }}
              type="radio"
              value="member_of_registered_tribe"
              checked={exemptionsFromWithholding === 'member_of_registered_tribe'}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setExemptionsFromWithholding(e.target.value);
              }}
            />
            <Box ml={2} />
            I am an enrolled member of a registered tribe, I live on the reservation
            of that tribe, AND I claim exemption from withholding on the wages derived
            from work performed on the reservation where I live.
          </Flex>
        </label>
        <label>
          <Flex mb="8px">
            <S.Input
              style={{ height: '22px' }}
              type="radio"
              value="member_of_reserve_national_guard"
              checked={exemptionsFromWithholding === 'member_of_reserve_national_guard'}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setExemptionsFromWithholding(e.target.value);
              }}
            />
            <Box ml={2} />
            I am a member of the Reserve or National Guard, and I claim to be exempt
            from withholding on my compensation determined under USC Title 10.
          </Flex>
        </label>
        <label>
          <Flex mb="8px">
            <S.Input
              style={{ height: '22px' }}
              type="radio"
              value="resident_north_dakota"
              checked={exemptionsFromWithholding === 'resident_north_dakota'}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setExemptionsFromWithholding(e.target.value);
              }}
            />
            <Box ml={2} />
            I am a resident of North Dakota, and claim exemption from withholding on my wages.
          </Flex>
        </label>
        <label>
          <Flex mb="8px">
            <S.Input
              style={{ height: '22px' }}
              type="radio"
              value="spouse_military"
              checked={exemptionsFromWithholding === 'spouse_military'}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setExemptionsFromWithholding(e.target.value);
              }}
            />
            <Box ml={2} />
            I am a resident of another state living in Montana solely to be with my spouse,
            who is a resident of the same state and a member of the U.S. armed forces
            assigned to a military location in Montana, and I claim exemption from withholding
            on my wages.
          </Flex>
        </label>
        <label>
          <Flex>
            <S.Input
              style={{ height: '22px' }}
              type="radio"
              value="not_applicable"
              checked={exemptionsFromWithholding === 'not_applicable'}
              disabled={isReviewStep}
              width="auto"
              onChange={(e) => {
                setExemptionsFromWithholding(e.target.value);
              }}
            />
            <Box ml={2} />
            Not applicable.
          </Flex>
        </label>
      </S.QuestionWrapper>
    </Flex>
  );

  const renderAllowances = (isReviewStep) => (
    <Flex column mb="20px">
      <Text bold fontSize="14px" mb="12px">
        Allowances
      </Text>
      <S.QuestionWrapper mb="15px">
        <Flex mb="7px">
          <S.QuestionLabel>
            Total number of allowances:
          </S.QuestionLabel>
        </Flex>
        <Flex mb="10px">
          <S.Input
            type="text"
            value={calculateTotalNoAllowances()}
            disabled
          />
        </Flex>
      </S.QuestionWrapper>
      <S.QuestionWrapper mb="15px">
        <Flex mb="7px">
          <S.QuestionLabel>
            Check the applicable boxes below to determine your Montana allowances.
          </S.QuestionLabel>
        </Flex>
        <Flex column>
          <label>
            <Flex mb="8px">
              <S.Input
                style={{ height: '22px' }}
                type="checkbox"
                value={forYourself}
                checked={forYourself}
                disabled={isReviewStep}
                onChange={(e) => setForYourself(e.target.checked)}
              />
              <Box ml={2} />
              For yourself
            </Flex>
          </label>
          <label>
            <Flex mb="8px">
              <S.Input
                style={{ height: '22px' }}
                type="checkbox"
                value={onlyOneJob}
                checked={onlyOneJob}
                disabled={isReviewStep}
                onChange={(e) => setOnlyOneJob(e.target.checked)}
              />
              <Box ml={2} />
              If you only have one job
            </Flex>
          </label>
          <label>
            <Flex mb="8px">
              <S.Input
                style={{ height: '22px' }}
                type="checkbox"
                value={forSpouse}
                checked={forSpouse}
                disabled={isReviewStep}
                onChange={(e) => setForSpouse(e.target.checked)}
              />
              <Box ml={2} />
              For your spouse if you expect to file as married filing jointly on your
              Montana tax return. But, you may choose not to check this box if you
              have a working spouse. (Not checking this may help you avoid having too
              little tax withheld.)
            </Flex>
          </label>
          <label>
            <Flex mb="8px">
              <S.Input
                style={{ height: '22px' }}
                type="checkbox"
                value={fileHeadOfHousehold}
                checked={fileHeadOfHousehold}
                disabled={isReviewStep}
                onChange={(e) => setFileHeadOfHousehold(e.target.checked)}
              />
              <Box ml={2} />
              If you will file as head of household on your Montana tax return
            </Flex>
          </label>
          <label>
            <Flex mb="8px">
              <S.Input
                style={{ height: '22px' }}
                type="checkbox"
                value={itemizedDeductions}
                checked={itemizedDeductions}
                disabled={isReviewStep}
                onChange={(e) => setItemizedDeductions(e.target.checked)}
              />
              <Box ml={2} />
              If you expect to report large itemized deductions (medical, child and dependent care, etc.)
              (Caution: An additional allowance could result in tax due when you file your return.)
            </Flex>
          </label>
        </Flex>
      </S.QuestionWrapper>
      <FormConsumer
        questions={allowancesQuestions}
        {...(isReviewStep && { viewResultAsManager: true, reviewActions: [] })}
        onQuestionChange={({ question, value }) => {
          const questions = allowancesQuestions.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setAllowancesQuestions(questions);
        }}
        ref={allowancesForm}
      />
    </Flex>
  );

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderStep1Form()}
          {renderExemptionsFromWithholding()}
          {mustCompleteAllowances() && renderAllowances()}
        </Flex>
        {viewResultAsManager && companyStaff.pii_permission === 'full_access' && (
          <Flex column mt={10} mb={10}>
            <Flex alignItems="center">
              <Text mr={16}>Download signed documents</Text>
              <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                <img src="/j/images/onboarding/download.svg" />
              </a>
            </Flex>
          </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={() => {
            let err = null;
            if (step1Form && step1Form.current) {
              step1Form.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (allowancesForm && allowancesForm.current) {
              allowancesForm.current.validate((error) => {
                if (error) err = error;
              });
            }
            if (err) {
              return;
            }
            // No error, proceed to save
            // - save answers to server
            // - processed to next step
            const stepOneData = stepOneQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            let forYourselfTemp = false;
            let onlyOneJobTemp = false;
            let forSpouseTemp = false;
            let fileHeadOfHouseholdTemp = false;
            let largeItemizedDeductionsTemp = false;
            let totalNumOfAllowancesTemp = '';
            let allowancesData = resetInitialQuestions(initialAllowancesQuestions).reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            if (mustCompleteAllowances()) {
              forYourselfTemp = forYourself;
              onlyOneJobTemp = onlyOneJob;
              forSpouseTemp = forSpouse;
              fileHeadOfHouseholdTemp = fileHeadOfHousehold;
              largeItemizedDeductionsTemp = itemizedDeductions;
              totalNumOfAllowancesTemp = calculateTotalNoAllowances().toString();

              allowancesData = allowancesQuestions.reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
            }
            const allowancesCheckboxData = {
              for_yourself: forYourselfTemp,
              only_one_job: onlyOneJobTemp,
              for_spouse: forSpouseTemp,
              file_head_of_household: fileHeadOfHouseholdTemp,
              large_itemized_deductions: largeItemizedDeductionsTemp,
              total_number_of_allowances: totalNumOfAllowancesTemp,
            };
            const exemptionData = { exemption: exemptionsFromWithholding };
            const data = {
              ...stepOneData, ...allowancesCheckboxData, ...allowancesData, ...exemptionData,
            };
            saveFormData(data, () => {
              setCurrentStep(STEPS.step2);
              scrollTop();
            });
          }}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderStep1Form(true)}
        {renderExemptionsFromWithholding(true)}
        {mustCompleteAllowances() && renderAllowances(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default Version2018;
